import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getFaxLogs } from '../../../routes/hacker/users';

// MUI Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';

const FaxLogsList: React.FC = () => {
    const { t } = useTranslation();
    const colors = useAppSelector(state => state.theme.colors);
    const [logs, setLogs] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchLogs = async () => {
        setIsLoading(true);
        const res = await getFaxLogs();
        if (res?.data.status === 200) {
            setLogs(res.data.logs);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const getStatusChip = (status: string, failureReason?: string) => {
        let color: "success" | "warning" | "error" | "info" = "info";
        let label = status;

        switch (status) {
            case 'delivered':
                color = 'success';
                break;
            case 'failed':
                color = 'error';
                label = `Failed: ${failureReason || 'Unknown'}`;
                break;
            case 'queued':
            case 'media.processed':
            case 'sending':
                color = 'warning';
                break;
        }
        return <Chip label={label} color={color} size="small" />;
    };

    return (
        <Box sx={{ 
            p: 3, 
            width: 'calc(100% - 125px)', 
            height: 'calc(100% - 40px)', 
            overflowY: 'auto',
            marginLeft: '125px',
            marginTop: '40px'
        }}>
            {/* FIX: Force a dark color for the title, ignoring the broken theme. */}
            <Typography variant="h5" sx={{ mb: 2, color: 'rgba(0, 0, 0, 0.87)' }}>
                Fax Logs
            </Typography>
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="fax logs table">
                        <TableHead>
                            <TableRow>
                                {/* FIX: Force dark, bold text for table headers */}
                                <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>From</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>To</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.87)' }}>Fax ID</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow key={log._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    {/* FIX: Force dark text for table body cells */}
                                    <TableCell sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>{moment(log.createdAt).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                                    <TableCell sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>{log.eventType}</TableCell>
                                    <TableCell sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>{log.fromNumber}</TableCell>
                                    <TableCell sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>{log.toNumber}</TableCell>
                                    <TableCell>{getStatusChip(log.status, log.failureReason)}</TableCell>
                                    <TableCell sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>{log.faxId}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default FaxLogsList;